import { DownloadOutlined, Search, StarBorder, ViewWeekOutlined } from '@mui/icons-material'
import { Box, Button, InputAdornment, MenuItem, Stack, Theme, Tooltip, Typography } from '@mui/material'
import {
  GridColDef,
  GridFilterModel,
  GridSlotsComponentsProps,
  GridToolbarColumnsButton,
  GridToolbarExport,
  GridToolbarExportContainer,
  GridToolbarQuickFilter,
  gridFilterModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium'
import uniq from 'lodash/uniq'
import { MouseEvent, PropsWithChildren, ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { filterItemIsValid } from '@omnea-app/packages/universal/saved-filters/validateFilterModel'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { AiFilterCreator } from '../../ai-filter-creator/AiFilterCreator'
import { useGridShowsSkeleton } from '../../data-grid/hooks/useGridShowsSkeleton'
import { RowGrouping } from '../../row-grouping/RowGrouping'
import { ToolbarSurface } from '../../ToolbarSurface'
import { useSavedFilterContext } from './SavedFilterContext'
import { ToolbarFiltersButton } from './ToolbarFiltersButton'

export type ExportOptions = {
  fileName: string
  fields?: string[]
}

export type ExportFormat = 'csv' | 'excel' | 'print'

declare module '@mui/x-data-grid-premium' {
  interface ToolbarPropsOverrides {
    tableName: string
    enableAiFilter?: boolean
    exportOptions?: ExportOptions
    displayRowGroupingButton?: boolean
    hideSearchBar?: boolean
    disableExportInToolbar?: boolean
    exportLoading?: boolean
    hideControlsRow?: boolean
    header?: ReactNode
    actions?: ReactNode
    onExportClick?: (event: MouseEvent<HTMLElement>) => void
    // Keeps the grid's own export button and menu but hands the chosen format to the caller instead of exporting
    // the rows the grid holds, for a grid showing one server page that must load the rest first.
    onExportFormat?: (format: ExportFormat) => void
  }
}

export const ControlledTableToolbar = ({
  tableName,
  enableAiFilter,
  exportOptions,
  displayRowGroupingButton,
  hideSearchBar,
  disableExportInToolbar,
  exportLoading,
  header,
  actions,
  hideControlsRow,
  onExportClick,
  onExportFormat,
}: NonNullable<GridSlotsComponentsProps['toolbar']>) => {
  const { t } = useTranslation('common')
  const { savedFilter, onUnselectSavedFilter } = useSavedFilterContext()
  const apiRef = useGridApiContext()
  const { GEN_AI } = useFeatureFlags()
  const showsSkeleton = useGridShowsSkeleton()

  const areColumnsGroupable = useMemo(() => {
    const columns = apiRef.current?.getAllColumns()
    return columns?.some((column) => column.groupable)
  }, [apiRef])

  const filterModel = useGridSelector(apiRef, gridFilterModelSelector)

  const filteredColumnsNames = getFilteredColumnNames(filterModel, apiRef.current?.getColumn)

  const clearFilters = () => {
    apiRef.current?.setFilterModel({ items: [] })
    if (savedFilter) {
      onUnselectSavedFilter()
    }
  }

  const exportButtonSlotProps = {
    button: {
      color: 'secondary',
      variant: 'text',
      disabled: exportLoading || showsSkeleton,
      startIcon: <DownloadOutlined />,
    },
  } as const

  if (hideControlsRow) return isDefined(header) ? <ToolbarSurface>{header}</ToolbarSurface> : null

  return (
    <ToolbarSurface>
      {header && header}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={(theme) => ({ padding: theme.spacing(2.5, 2.5, 2.5, 3) })}
      >
        <Stack
          sx={{
            flexDirection: 'row',
            gap: 2,
          }}
        >
          {savedFilter && <SavedFilterBadge>{savedFilter.name}</SavedFilterBadge>}

          <ToolbarFiltersButton
            filteredColumnNames={filteredColumnsNames}
            disabled={showsSkeleton}
            onClearFilters={clearFilters}
          />
          {GEN_AI && enableAiFilter && (
            // oxlint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: bulk migration to add error
            <AiFilterCreator tableName={tableName!} apiRef={apiRef} disabled={showsSkeleton} />
          )}
          {displayRowGroupingButton && areColumnsGroupable && <RowGrouping disabled={showsSkeleton} />}
          <Tooltip title={t('table.table-toolbar.tooltip-columns')}>
            <GridToolbarColumnsButton
              slotProps={{
                button: {
                  color: 'secondary',
                  variant: 'text',
                  disabled: showsSkeleton,
                  startIcon: <ViewWeekOutlined />,
                },
              }}
            />
          </Tooltip>
          {!disableExportInToolbar && (
            <Tooltip title={exportLoading ? t('table.table-toolbar.tooltip-export-loading') : ''}>
              <span style={{ display: 'flex' }}>
                {onExportClick != null ? (
                  <Button
                    color="secondary"
                    variant="text"
                    size="small"
                    disabled={exportLoading || showsSkeleton}
                    onClick={onExportClick}
                    startIcon={<DownloadOutlined />}
                  >
                    {t('table.table-toolbar.export-button-label')}
                  </Button>
                ) : onExportFormat != null ? (
                  <GridToolbarExportContainer slotProps={exportButtonSlotProps}>
                    {EXPORT_FORMATS.map(({ format, labelKey }) => (
                      <ExportFormatMenuItem
                        key={format}
                        label={apiRef.current.getLocaleText(labelKey)}
                        onSelect={() => onExportFormat(format)}
                      />
                    ))}
                  </GridToolbarExportContainer>
                ) : (
                  <GridToolbarExport csvOptions={exportOptions} excelOptions={exportOptions} slotProps={exportButtonSlotProps} />
                )}
              </span>
            </Tooltip>
          )}
          {actions}
        </Stack>
        {!hideSearchBar && (
          <GridToolbarQuickFilter
            placeholder={t('search')}
            debounceMs={500}
            sx={{ marginBottom: 0 }}
            InputProps={{
              startAdornment: SearchStartAdornment,
              endAdornment: null,
              size: 'small',
            }}
          />
        )}
      </Stack>
    </ToolbarSurface>
  )
}

const SearchStartAdornment = (
  <InputAdornment position="start">
    <Search sx={{ width: 18 }} />
  </InputAdornment>
)

const getFilteredColumnNames = (
  filterModel: GridFilterModel | undefined,
  getColumn: undefined | ((field: string) => GridColDef)
): string[] | null => {
  if (!filterModel || !getColumn) return null
  const filterItems = filterModel?.items

  if (!filterItems) return null

  const allFilteredColumnNames = filterItems.filter(filterItemIsValid).map(({ field }) => field)

  if (allFilteredColumnNames.length < 1) return null

  const uniqueNames = uniq(allFilteredColumnNames)
    .map(getColumn)
    .map((columnDef) => columnDef?.headerName)
    .filter(isDefined)

  if (uniqueNames.length < 1) return null

  return uniqueNames
}

const SavedFilterBadge: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <Box
      sx={(theme: Theme) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '2px',

        padding: '3px 6px',
        color: theme.palette.fg.inverted,
        backgroundColor: theme.palette.bg.inverted,
      })}
    >
      <StarBorder sx={{ width: 16, height: 16 }} />
      <Typography variant="small-medium">{children}</Typography>
    </Box>
  )
}

// Same order and labels as the grid's own export menu, which sorts its items by name.
const EXPORT_FORMATS = [
  { format: 'csv', labelKey: 'toolbarExportCSV' },
  { format: 'excel', labelKey: 'toolbarExportExcel' },
  { format: 'print', labelKey: 'toolbarExportPrint' },
] as const

const ExportFormatMenuItem = ({
  label,
  onSelect,
  hideMenu,
}: {
  label: ReactNode
  onSelect: () => void
  hideMenu?: () => void
}) => (
  <MenuItem
    onClick={() => {
      onSelect()
      hideMenu?.()
    }}
  >
    {label}
  </MenuItem>
)
